import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SymbolSearchPopup from "./SymbolSearchPopup";

const HOLD_ACCESS_STORAGE_KEY = "holdAccessContext";

const getStoredHoldAccessContext = () => {
  try {
    const storedValue = sessionStorage.getItem(HOLD_ACCESS_STORAGE_KEY);
    if (!storedValue) {
      return { uid: "", from_admin: false };
    }
    const parsed = JSON.parse(storedValue);
    return {
      uid: String(parsed?.uid || ""),
      from_admin:
        parsed?.from_admin === 1 ||
        parsed?.from_admin === "1" ||
        parsed?.from_admin === true,
    };
  } catch {
    return { uid: "", from_admin: false };
  }
};

const legContractsOf = (item) => Number(item?.contracts ?? item?.size ?? 0) || 0;

/**
 * TFH HoldAdminManualModal 과 동작 동일. Bitsoopai 는 uid_TFH 필드 없음 — 대상 회원은 서버가 쿠키(admin_target_uid)로만 식별 (요청 바디에 uid 불필요).
 */
export default function HoldAdminManualModal({
  open,
  payload,
  onClose,
  onOrderSuccess,
  fetchHold,
  hasSecondAccount,
  pageSymbol,
  selectableSymbols,
  holdTargetUid,
  isAdminAccess,
  pageLeverage,
  manualOrderEndpoint = "/hold/admin_manual_order",
}) {
  const [newOrderUsdt, setNewOrderUsdt] = useState("");
  const [newLeverage, setNewLeverage] = useState("20");
  const [legInputMode, setLegInputMode] = useState("percent");
  const [legInputValue, setLegInputValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [newAccount, setNewAccount] = useState("main");
  const [newSide, setNewSide] = useState("long");
  const [newSymbol, setNewSymbol] = useState("");
  const [symbolSearchOpen, setSymbolSearchOpen] = useState(false);
  const panelRef = useRef(null);
  const [anchoredStyle, setAnchoredStyle] = useState(null);

  const symbolOptions = useMemo(() => {
    const raw = Array.isArray(selectableSymbols) ? selectableSymbols : [];
    const merged = new Set(raw.filter((s) => s && s !== "default"));
    const ns = String(newSymbol || "").trim();
    if (ns) {
      merged.add(ns);
    }
    return Array.from(merged).sort((a, b) => a.localeCompare(b, "en"));
  }, [selectableSymbols, newSymbol]);

  useEffect(() => {
    if (!open || !payload) {
      return;
    }
    setNewOrderUsdt("");
    setLegInputMode("percent");
    setLegInputValue("");
    setBusy(false);
    setSymbolSearchOpen(false);
    if (payload.mode === "new") {
      setNewAccount("main");
      setNewSide("long");
      const levDefault = Number(pageLeverage);
      setNewLeverage(
        Number.isFinite(levDefault) && levDefault >= 1 ? String(Math.round(levDefault)) : "20"
      );
      const ps = String(pageSymbol || "").trim();
      if (ps && ps !== "default") {
        setNewSymbol(ps);
      } else {
        setNewSymbol("");
      }
    }
  }, [open, payload, pageSymbol, pageLeverage]);

  const hasAnchor =
    open &&
    payload &&
    payload.anchorRect != null &&
    typeof payload.anchorRect.top === "number" &&
    typeof payload.anchorRect.left === "number";

  useLayoutEffect(() => {
    if (!open || !payload) {
      setAnchoredStyle(null);
      return;
    }
    const ar = payload.anchorRect;
    if (!ar || typeof ar.top !== "number" || typeof ar.left !== "number") {
      setAnchoredStyle(null);
      return;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const panelW = Math.min(400, vw - 16);
    let top = (ar.bottom != null ? ar.bottom : ar.top + (ar.height || 0)) + 8;
    let left = ar.left;
    left = Math.max(8, Math.min(left, vw - panelW - 8));
    const estH = panelRef.current?.offsetHeight || 440;
    if (top + estH > vh - 8) {
      top = Math.max(8, (ar.top != null ? ar.top : 0) - estH - 8);
    }
    setAnchoredStyle({
      position: "fixed",
      top,
      left,
      width: panelW,
      zIndex: 1001,
      maxHeight: "calc(100vh - 16px)",
      overflow: "auto",
    });
  }, [open, payload]);

  const legPercentPreview = useMemo(() => {
    if (!open || !payload || payload.mode !== "leg" || legInputMode !== "percent") {
      return null;
    }
    const rowItem = payload.item;
    if (!rowItem) {
      return null;
    }
    const p = parseFloat(String(legInputValue).replace(",", "."));
    if (!Number.isFinite(p) || p <= 0) {
      return null;
    }
    const c = legContractsOf(rowItem);
    const m = Number(rowItem.margin) || 0;
    if (c <= 0) {
      return null;
    }
    const deltaContracts = c * (p / 100);
    const deltaMarginUsdt = m * (p / 100);
    return { pct: p, deltaContracts, deltaMarginUsdt, currentContracts: c };
  }, [open, payload, legInputMode, legInputValue]);

  if (!open || !payload) {
    return null;
  }

  const isNew = payload.mode === "new";
  const account = isNew ? newAccount : payload.account;
  const item = !isNew ? payload.item : null;
  const sym = isNew ? String(newSymbol || "").trim() : String(item?.symbol || "").trim();
  const positionSide = isNew ? newSide : item?.side;
  const accLabel = account === "second" ? "보조" : "메인";
  const sideLabel = positionSide === "long" ? "롱" : "숏";

  const submit = async (action) => {
    if (busy) {
      return;
    }
    if (isNew && action !== "open") {
      return;
    }
    if (account === "second" && !hasSecondAccount) {
      alert("보조계정이 연동되어 있지 않습니다.");
      return;
    }
    if (!sym || sym === "default") {
      alert(isNew ? "심볼을 선택하세요." : "심볼을 확인할 수 없습니다.");
      return;
    }

    const storedAccess = getStoredHoldAccessContext();
    const uidForHold = String(holdTargetUid || storedAccess.uid || "").trim();
    const effectiveAdminAccess = isAdminAccess || storedAccess.from_admin;
    if (effectiveAdminAccess && !uidForHold) {
      alert(
        "대상 회원 UID가 없습니다. 관리자 메뉴에서 회원을 선택한 뒤 홀드 페이지로 들어왔는지 확인해 주세요."
      );
      return;
    }

    let requestAmount;
    let amountBasis;
    let confirmDetail;
    let newEntryLeverage = null;

    if (isNew) {
      const lev = parseInt(String(newLeverage).replace(",", ".").trim(), 10);
      if (!Number.isFinite(lev) || lev < 1 || lev > 200) {
        alert("레버리지는 1~200 사이 정수로 입력하세요.");
        return;
      }
      newEntryLeverage = lev;
      const amount = parseFloat(String(newOrderUsdt).replace(",", "."));
      if (!Number.isFinite(amount) || amount <= 0) {
        alert("USDT(진입 마진) 금액을 입력하세요.");
        return;
      }
      const notionalApprox = amount * lev;
      if (notionalApprox + 1e-9 < 5) {
        alert(
          "Bitget USDT-M 시장가는 주문 명목(진입마진×레버)이 약 5 USDT 미만이면 거절됩니다. 마진·레버를 조정해 주세요."
        );
        return;
      }
      requestAmount = amount;
      amountBasis = "usdt";
      confirmDetail = `레버 ${lev}x · 진입(마진) USDT ${amount} (명목 약 ${notionalApprox.toFixed(2)} USDT)`;
    } else if (legInputMode === "percent") {
      let p = parseFloat(String(legInputValue).replace(",", "."));
      if (!Number.isFinite(p) || p <= 0) {
        alert("비율(%)을 입력하세요.");
        return;
      }
      if (action === "reduce" && p > 100) {
        p = 100;
      }
      const c = legContractsOf(item);
      if (c <= 0) {
        alert("보유 계약이 없습니다.");
        return;
      }
      let contractsAmt = c * (p / 100);
      if (action === "reduce") {
        contractsAmt = Math.min(contractsAmt, c);
      }
      if (!Number.isFinite(contractsAmt) || contractsAmt <= 0) {
        alert("적용할 계약 수가 없습니다.");
        return;
      }
      requestAmount = contractsAmt;
      amountBasis = "contracts";
      const pctLabel =
        action === "reduce" && parseFloat(String(legInputValue).replace(",", ".")) > 100
          ? "100"
          : String(legInputValue);
      confirmDetail = `비율 ${pctLabel}% → 계약 수 약 ${contractsAmt.toFixed(6)} (거래소 반올림 적용)`;
    } else {
      const amount = parseFloat(String(legInputValue).replace(",", "."));
      if (!Number.isFinite(amount) || amount <= 0) {
        alert("USDT(진입 마진) 금액을 입력하세요.");
        return;
      }
      let levUi = null;
      const pageSym = String(pageSymbol || "").trim();
      const rowSym = String(sym || "").trim();
      if (pageSym && rowSym === pageSym) {
        const pl = Number(pageLeverage);
        if (Number.isFinite(pl) && pl >= 1 && pl <= 200) {
          levUi = Math.trunc(pl);
        }
      }
      if (levUi == null) {
        const fromRow = item?.leverage;
        const n = Number(fromRow);
        if (Number.isFinite(n) && n >= 1 && n <= 200) {
          levUi = Math.trunc(n);
        }
      }
      if (levUi != null) {
        const notionalLeg = amount * levUi;
        if (notionalLeg + 1e-9 < 5) {
          alert(
            "현재 페이지/포지션 기준으로 보면 주문 명목(진입마진×레버)이 약 5 USDT 미만입니다. 수치를 늘리거나, 레버/심볼이 맞는지 확인해 주세요."
          );
          return;
        }
      }
      requestAmount = amount;
      amountBasis = "usdt";
      confirmDetail =
        levUi != null
          ? `진입(마진) USDT ${amount} (명목 약 ${(amount * levUi).toFixed(2)} USDT, 레버 ${levUi}x 기준)`
          : `진입(마진) USDT ${amount} (서버가 레버를 해석해 계약 수로 환산)`;
    }

    const actLabel = action === "open" ? "증액·신규" : "감액(일부)";
    if (!window.confirm(`[관리자 수동] ${accLabel} / ${sym} / ${sideLabel} / ${actLabel}\n${confirmDetail}\n— 진행할까요?`)) {
      return;
    }
    setBusy(true);
    try {
      const legLevForBody = (() => {
        if (isNew || legInputMode !== "usdt" || !item) {
          return null;
        }
        const pageSym = String(pageSymbol || "").trim();
        const rowSym = String(sym || "").trim();
        if (pageSym && rowSym === pageSym) {
          const pl = Number(pageLeverage);
          if (Number.isFinite(pl) && pl >= 1 && pl <= 200) {
            return Math.trunc(pl);
          }
        }
        const n = Number(item?.leverage);
        if (Number.isFinite(n) && n >= 1 && n <= 200) {
          return Math.trunc(n);
        }
        return null;
      })();
      const response = await fetchHold(manualOrderEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account,
          symbol: sym,
          positionSide,
          action,
          amount: requestAmount,
          amount_basis: amountBasis,
          entry_intent: isNew ? "new" : "leg",
          ...(newEntryLeverage != null ? { leverage: newEntryLeverage } : {}),
          ...(!isNew && legInputMode === "usdt" && legLevForBody != null ? { leverage: legLevForBody } : {}),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error(data.message || "주문 실패");
      }
      const extra =
        data.contracts_ordered != null ? `\n주문 계약 수(반올림): ${data.contracts_ordered}` : "";
      const levExtra = data.leverage_set != null ? `\n적용 레버리지: ${data.leverage_set}x` : "";
      let doneMsg = `${data.message || "주문이 접수되었습니다."}${levExtra}${extra}`;
      const ver = data.verification;
      if (ver && action === "open" && ver.ok === false) {
        doneMsg += `\n\n[주의] 포지션 자동 검증 실패\n${ver.hint || "서버 로그(포지션 검증·교차확인)를 확인하세요."}`;
      }
      if (ver?.main_second_same_api_key) {
        doneMsg +=
          "\n\n[참고] DB에 메인·보조 API 키가 동일합니다. 항상 같은 Bitget 계정으로 주문됩니다.";
      }
      alert(doneMsg);
      onOrderSuccess?.();
      onClose();
    } catch (e) {
      console.error(e);
      alert(e.message || "주문 오류");
    } finally {
      setBusy(false);
    }
  };

  const displayContracts = !isNew ? legContractsOf(item) : null;

  const onBackdrop = () => {
    if (!busy) {
      onClose();
    }
  };

  const panelClassName = `popup hold-admin-manual-modal${
    hasAnchor ? " hold-admin-manual-modal--anchored" : ""
  }`;

  const panel = (
    <div
      ref={panelRef}
      className={panelClassName}
      role="dialog"
      aria-modal="true"
      style={hasAnchor && anchoredStyle ? anchoredStyle : undefined}
      onClick={(e) => e.stopPropagation()}
    >
        <h3>관리자 수동 주문</h3>
        {isNew ? (
          <div className="hold-admin-manual-modal__meta">
            신규 진입할 <strong>Bitget USDT 선물</strong> 심볼을 아래에서 고르거나 검색하세요.
          </div>
        ) : (
          <div className="hold-admin-manual-modal__meta">
            {accLabel} · <strong>{sym}</strong> · {sideLabel}
            <br />
            보유 계약 {displayContracts != null ? displayContracts.toFixed(4) : "-"} / 진입마진{" "}
            {item?.margin != null ? Number(item.margin).toFixed(3) : "-"}
            <br />
            평단 {item?.entryPrice != null ? Number(item.entryPrice).toFixed(4) : "-"} · 미실현{" "}
            {item?.unrealizedPnl != null ? Number(item.unrealizedPnl).toFixed(3) : "-"}
          </div>
        )}
        {isNew ? (
          <div className="hold-admin-manual-modal__row">
            <label className="hold-admin-manual-modal__label">심볼</label>
            <div className="hold-admin-manual-modal__symbol-pick">
              <select
                className="search-input hold-admin-manual-modal__select"
                value={newSymbol || ""}
                onChange={(e) => setNewSymbol(e.target.value)}
                disabled={busy}
              >
                <option value="">선택…</option>
                {symbolOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="button_benefit_c_lever hold-admin-manual-modal__symbol-search-btn"
                disabled={busy}
                onClick={() => setSymbolSearchOpen(true)}
              >
                검색
              </button>
            </div>
          </div>
        ) : null}
        {isNew ? (
          <div className="hold-admin-manual-modal__row">
            <label className="hold-admin-manual-modal__label">계정</label>
            <select
              className="search-input hold-admin-manual-modal__select"
              value={newAccount}
              onChange={(e) => setNewAccount(e.target.value)}
              disabled={busy}
            >
              <option value="main">메인</option>
              <option value="second" disabled={!hasSecondAccount}>
                보조
              </option>
            </select>
          </div>
        ) : null}
        {isNew ? (
          <div className="hold-admin-manual-modal__row">
            <label className="hold-admin-manual-modal__label">방향</label>
            <select
              className="search-input hold-admin-manual-modal__select"
              value={newSide}
              onChange={(e) => setNewSide(e.target.value)}
              disabled={busy}
            >
              <option value="long">롱</option>
              <option value="short">숏</option>
            </select>
          </div>
        ) : null}
        {isNew ? (
          <div className="hold-admin-manual-modal__row">
            <label className="hold-admin-manual-modal__label" htmlFor="hold-admin-new-lev-input">
              레버리지 (배)
            </label>
            <input
              id="hold-admin-new-lev-input"
              type="number"
              min={1}
              max={200}
              step={1}
              className="search-input"
              placeholder="예: 20"
              value={newLeverage}
              onChange={(e) => setNewLeverage(e.target.value)}
              disabled={busy}
            />
          </div>
        ) : null}
        {!isNew ? (
          <div className="hold-admin-manual-modal__mode-toggle" role="group" aria-label="수량 입력 방식">
            <label className="hold-admin-manual-modal__radio">
              <input
                type="radio"
                name="hold-admin-leg-mode"
                checked={legInputMode === "percent"}
                onChange={() => setLegInputMode("percent")}
                disabled={busy}
              />
              비율 (%)
            </label>
            <label className="hold-admin-manual-modal__radio">
              <input
                type="radio"
                name="hold-admin-leg-mode"
                checked={legInputMode === "usdt"}
                onChange={() => setLegInputMode("usdt")}
                disabled={busy}
              />
              USDT (진입 마진)
            </label>
          </div>
        ) : null}
        {isNew ? (
          <div className="hold-admin-manual-modal__row">
            <label className="hold-admin-manual-modal__label" htmlFor="hold-admin-new-usdt-input">
              USDT (진입 마진)
            </label>
            <input
              id="hold-admin-new-usdt-input"
              type="text"
              inputMode="decimal"
              className="search-input"
              placeholder="예: 50"
              value={newOrderUsdt}
              onChange={(e) => setNewOrderUsdt(e.target.value)}
              disabled={busy}
            />
          </div>
        ) : (
          <div className="hold-admin-manual-modal__row">
            <label className="hold-admin-manual-modal__label" htmlFor="hold-admin-leg-input">
              {legInputMode === "percent" ? "비율 (%)" : "USDT (진입 마진)"}
            </label>
            <input
              id="hold-admin-leg-input"
              type="text"
              inputMode="decimal"
              className="search-input"
              placeholder={legInputMode === "percent" ? "예: 25 (= 보유의 25%)" : "예: 50"}
              value={legInputValue}
              onChange={(e) => setLegInputValue(e.target.value)}
              disabled={busy}
            />
          </div>
        )}
        {!isNew && legInputMode === "percent" && legPercentPreview ? (
          <div className="hold-admin-manual-modal__preview">
            <div className="hold-admin-manual-modal__preview-title">적용 예정 (산출)</div>
            <div>변동 계약 수: 약 {legPercentPreview.deltaContracts.toFixed(4)}</div>
            <div>진입마진 동일 비율(참고): 약 {legPercentPreview.deltaMarginUsdt.toFixed(3)} USDT</div>
            {parseFloat(String(legInputValue).replace(",", ".")) > 100 ? (
              <div className="hold-admin-manual-modal__preview-note">감액 시 비율은 최대 100%까지 적용됩니다.</div>
            ) : null}
          </div>
        ) : null}
        <p className="hold-admin-manual-modal__hint">
          {isNew
            ? "USDT는 진입(마진) 기준이며 ×레버리지 ≈ 주문 명목입니다. Bitget setLeverage 후 시장가 주문합니다. 심볼당 최대 배율은 거래소 제한을 따릅니다. 명목이 약 5 USDT 미만이면 거절됩니다(45110)."
            : legInputMode === "usdt"
              ? "USDT(진입 마진)×레버리지(요청/포지션/거래소 조회)로 계약 수를 맞춥니다. 주문 명목이 약 5 USDT 미만이면 거절될 수 있어, 수치·레버를 충분히 잡아 주세요."
              : "비율은 현재 보유 계약 수 기준입니다. 진입마진 참고값은 포지션과 같은 비율로 움직인다고 가정한 추정치입니다."}
        </p>
        <div className="hold-admin-manual-modal__actions">
          {isNew ? (
            <button type="button" className="button_benefit_c_lever" disabled={busy} onClick={() => submit("open")}>
              신규 진입
            </button>
          ) : (
            <>
              <button type="button" className="button_benefit_c_lever" disabled={busy} onClick={() => submit("open")}>
                증액
              </button>
              <button type="button" className="button_benefit_c_lever" disabled={busy} onClick={() => submit("reduce")}>
                감액
              </button>
            </>
          )}
        </div>
        <div className="hold-admin-manual-modal__footer-close">
          <button type="button" className="close-button" disabled={busy} onClick={onClose}>
            닫기
          </button>
        </div>
        {isNew ? (
          <SymbolSearchPopup
            isOpen={symbolSearchOpen}
            onAdd={(picked) => {
              if (picked) {
                setNewSymbol(picked);
              }
              setSymbolSearchOpen(false);
            }}
            onClose={() => setSymbolSearchOpen(false)}
          />
        ) : null}
    </div>
  );

  return createPortal(
    hasAnchor ? (
      <>
        <div
          className="hold-admin-manual-modal__scrim"
          role="presentation"
          onClick={onBackdrop}
        />
        {panel}
      </>
    ) : (
      <div className="popup-backdrop" role="presentation" onClick={onBackdrop}>
        {panel}
      </div>
    ),
    document.body
  );
}
